import React from 'react';
import { motion } from 'framer-motion';
import { Compass, Hotel, Plane, Sparkles } from 'lucide-react';
import AxonallLogo from '../../components/AxonallLogo';

/* Same luxury hero asset family as Studio landing — reads clearly as a full-bleed backdrop */
const HERO_IMAGE =
    'https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/atlantis-the-palmjpg-1b19e1d1-b721-471b-b737-a08aea6cf0f5.jpg';

const features = [
    { icon: Compass, label: 'Experiences' },
    { icon: Hotel, label: 'Hotels' },
    { icon: Plane, label: 'Private jets' },
];

const AdamWelcome = () => {
    return (
        <div className="relative h-full min-h-0 w-full min-w-0 overflow-x-hidden overflow-y-auto bg-black font-['Inter',system-ui,sans-serif]">
            {/* Full-bleed background photography */}
            <motion.div
                initial={{ scale: 1.06 }}
                animate={{ scale: 1 }}
                transition={{ duration: 24, ease: 'linear', repeat: Infinity, repeatType: 'reverse' }}
                className="absolute inset-0 z-0"
            >
                <img
                    src={HERO_IMAGE}
                    alt=""
                    loading="eager"
                    decoding="async"
                    className="h-full w-full object-cover object-center"
                />
                {/* Readability scrims (Landing-style ~60% feel, but gradient preserves photo detail) */}
                <div className="absolute inset-0 bg-black/50" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/45" />
            </motion.div>

            {/* Ambient accents — aligned with dashboard / experiences heroes */}
            <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
                <motion.div
                    animate={{ x: ['-15%', '15%', '-15%'], opacity: [0.06, 0.14, 0.06] }}
                    transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute top-1/4 left-[-20%] w-[130%] h-72 bg-white/10 blur-[100px] rounded-full"
                />
                <motion.div
                    animate={{ x: ['10%', '-10%', '10%'], opacity: [0.05, 0.12, 0.05] }}
                    transition={{ duration: 28, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
                    className="absolute bottom-1/4 right-[-15%] w-[120%] h-80 bg-neutral-400/10 blur-[120px] rounded-full"
                />
            </div>

            <div className="relative z-10 min-h-0 h-full flex flex-col items-center justify-center px-4 sm:px-10 py-6 sm:py-10 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
                    className="relative w-full max-w-2xl rounded-[1.5rem] sm:rounded-[2rem] border border-white/10 bg-black/45 shadow-2xl shadow-black/50 backdrop-blur-2xl overflow-hidden min-w-0"
                >
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/40 to-transparent" />

                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" />

                    <div className="relative p-5 sm:p-12 md:p-14">
                        {/* Studio brand row — matches portal header language */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15, duration: 0.6 }}
                            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8"
                        >
                            <div className="flex items-center gap-3">
                                <AxonallLogo color="text-white" size="text-xl sm:text-2xl" />
                                <div className="hidden sm:block h-5 w-px bg-white/25" aria-hidden />
                                <span className="text-[10px] font-black uppercase tracking-[0.28em] text-white/45">
                                    Studio
                                </span>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.25, duration: 0.65 }}
                            className="mb-6 flex flex-col items-center gap-3"
                        >
                            <p className="flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-[0.35em] text-white/50">
                                <Sparkles className="w-3.5 h-3.5 text-white/60 shrink-0" aria-hidden />
                                AI concierge
                            </p>
                            <div className="flex flex-wrap items-center justify-center gap-3">
                                <h1 className="font-AXONALLPrimeRegular text-4xl sm:text-5xl md:text-6xl font-medium text-white tracking-tight">
                                    Seren
                                </h1>
                                <span className="px-2.5 py-1 rounded-full border border-white/20 text-[9px] font-semibold uppercase tracking-[0.2em] text-white/70 bg-white/5">
                                    Concierge
                                </span>
                            </div>
                            <div className="h-px w-14 bg-gradient-to-r from-transparent via-white/40 to-transparent mt-2" />
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.45, duration: 0.65 }}
                            className="font-AXONALLPrimeRegular text-lg sm:text-xl text-white/90 mb-4 tracking-tight"
                        >
                            Your intelligent travel planning partner
                        </motion.p>



                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.7, duration: 0.65 }}
                            className="grid grid-cols-3 gap-3 sm:gap-4 pt-8 border-t border-white/10"
                        >
                            {features.map(({ icon: Icon, label }) => (
                                <div
                                    key={label}
                                    className="flex flex-col items-center gap-2.5 rounded-2xl border border-white/10 bg-white/[0.04] px-2 py-4 sm:py-5 transition-colors duration-300 hover:bg-white/[0.07] hover:border-white/15"
                                >
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-black/30 text-white/85">
                                        <Icon className="w-4 h-4" strokeWidth={1.5} aria-hidden />
                                    </div>
                                    <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-white/45">
                                        {label}
                                    </span>
                                </div>
                            ))}
                        </motion.div>
                    </div>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.15, duration: 0.8 }}
                    className="mt-8 sm:mt-10 text-[10px] font-black uppercase tracking-[0.35em] text-white/35"
                >
                    <span className="inline-flex items-center gap-2">
                        <span className="inline-block h-1 w-1 rounded-full bg-white/40 animate-pulse" />
                        Start typing to begin
                    </span>
                </motion.p>
            </div>
        </div>
    );
};

export default AdamWelcome;
